import { useEffect, useRef } from 'react';
import { useRouterState } from '@tanstack/react-router';
import { usePopupMfeStore, openPopupMfe, closePopupMfe } from './popup-store';
import { isPopupMfe } from './popup-mfe-constants';

const PARAM = 'popup';
const PARAM_ROUTE = 'popupRoute';

function popupRouteAppId(pathname: string): string | null {
  const match = /^\/apps\/([^/]+)/i.exec(pathname);
  const appId = match?.[1]?.toLowerCase();
  return appId && isPopupMfe(appId) ? appId : null;
}

/**
 * Two-way sync between the active popup MFE and a `?popup=<appId>` URL search
 * param.
 *
 *   - Store → URL: opening a popup reflects it in the URL (shareable, survives
 *     refresh — legacy parity for the named `popup` outlet); closing clears it.
 *   - URL → Store: on initial load and back/forward navigation the param drives
 *     the store, so a deep link like `…/?popup=stf` opens the dialog.
 *
 * Writes use `replaceState` (never pushes a new entry) so Back doesn't re-open a
 * just-closed popup. The two effects can't loop: `replaceState` does not emit
 * `popstate`, and each guards against a no-op write/open.
 */
export function usePopupUrlSync(): void {
  const appId = usePopupMfeStore((s) => s.appId);
  const subPath = usePopupMfeStore((s) => s.subPath);
  const location = useRouterState({
    select: (s) => ({ pathname: s.location.pathname, searchStr: s.location.searchStr }),
  });
  const initialized = useRef(false);

  // Store → URL.
  useEffect(() => {
    if (!initialized.current) return;
    const url = new URL(window.location.href);
    const current = url.searchParams.get(PARAM);
    const currentRoute = url.searchParams.get(PARAM_ROUTE);
    if (appId) {
      if (current === appId && (currentRoute || null) === (subPath || null)) return;
      url.searchParams.set(PARAM, appId);
      if (subPath) {
        url.searchParams.set(PARAM_ROUTE, subPath);
      } else {
        url.searchParams.delete(PARAM_ROUTE);
      }
    } else {
      if (!current) return;
      url.searchParams.delete(PARAM);
      url.searchParams.delete(PARAM_ROUTE);
    }
    window.history.replaceState(window.history.state, '', url);
  }, [appId, subPath]);

  // URL → Store (initial load + browser back/forward).
  useEffect(() => {
    // On TanStack Router location changes (effect body): ONLY open popups (deep
    // links, initial mount). Never close. Angular's named popup outlet is
    // independent of the primary route — it persists across page navigations and
    // only closes via explicit closeModal(). The React equivalent: the store drives
    // close (Escape, header button, MFE calling EY_RAPToR_closeMFEModalPopup),
    // NOT the URL. This prevents the race where TanStack Router re-evaluates the
    // URL (stripping ?popup= momentarily) during the MFE load chain triggered by
    // the Store→URL replaceState.
    const params = new URLSearchParams(location.searchStr);
    const param = params.get(PARAM);
    const route = params.get(PARAM_ROUTE);
    if (param) {
      openPopupMfe(param, route || undefined);
    }
    initialized.current = true;

    // Browser Back/Forward: the only URL-driven close path. When the user presses
    // Back, the popstate fires with the PREVIOUS URL (which may lack ?popup=).
    // This is a genuine user action — close the popup.
    const onPopState = () => {
      const p = new URLSearchParams(window.location.search);
      const popupParam = p.get(PARAM);
      const popupRoute = p.get(PARAM_ROUTE);
      if (popupParam) {
        openPopupMfe(popupParam, popupRoute || undefined);
      } else if (!popupRouteAppId(window.location.pathname)) {
        closePopupMfe();
      }
    };
    window.addEventListener('popstate', onPopState);
    return () => window.removeEventListener('popstate', onPopState);
  }, [location.pathname, location.searchStr]);
}
